import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Camera, MapPin, FileText, Shield, Brain, AlertTriangle, Users, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface SimilarityMatch {
  report_id: string;
  similarity_score: number;
  match_factors: string[];
}

interface ScoreBreakdown {
  photo_present?: { points: number; max: number; earned: boolean };
  photo_confidence?: { points: number; max: number; ai_confidence: number };
  description_quality?: { 
    points: number; 
    max: number; 
    details: {
      location_specific: boolean;
      word_count_ok: boolean;
      work_activity: boolean;
      cause_provided: boolean;
    };
    word_count: number;
  };
  gps_location?: { points: number; max: number; earned: boolean };
  reporter_credibility?: { points: number; max: number; credibility_score: number };
  cause_analysis_bonus?: { points: number; max: number; earned: boolean; cause_category: string | null };
  total?: number;
  boosted_total?: number;
  similar_reports_boost?: number;
  peer_corroboration_boost?: number;
  similar_reports_count?: number;
  similarity_matches?: SimilarityMatch[];
  routing?: string;
}

interface ScoreBreakdownDisplayProps {
  score: number | null;
  breakdown: ScoreBreakdown | null;
  compact?: boolean;
}

export function ScoreBreakdownDisplay({ score, breakdown, compact = false }: ScoreBreakdownDisplayProps) {
  const [showSimilarityDetails, setShowSimilarityDetails] = useState(false);
  
  if (score === null) return <span className="text-muted-foreground">-</span>;

  const getScoreColor = (s: number) => {
    if (s >= 55) return "text-success";
    if (s >= 25) return "text-warning";
    return "text-destructive";
  };

  const getRoutingLabel = (routing: string | undefined) => {
    switch (routing) {
      case "hazard_confirmed": return "Auto-Confirmed";
      case "peer_corroboration_requested": return "Peer Corroboration";
      case "pending_verification": return "Pending";
      default: return routing || "Unknown";
    }
  };

  const getRoutingColor = (routing: string | undefined) => {
    switch (routing) {
      case "hazard_confirmed": return "bg-success text-success-foreground";
      case "peer_corroboration_requested": return "bg-warning text-warning-foreground";
      case "pending_verification": return "bg-destructive/20 text-destructive";
      default: return "bg-muted text-muted-foreground";
    }
  };

  const getMatchFactorLabel = (factor: string) => {
    const labels: Record<string, string> = {
      location: "Same Location",
      hazard_category: "Same Hazard Type",
      reported_worker: "Same Worker",
      hazard_classification: "Same Classification",
      recent_24h: "Within 24h",
      recent_48h: "Within 48h",
      recent_72h: "Within 72h"
    };
    return labels[factor] || factor;
  };

  // Compact view for table cells
  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <span className={cn("font-medium", getScoreColor(score))}>{score}/100</span>
        {breakdown?.routing && (
          <Badge variant="outline" className={cn("text-xs px-1.5 py-0", getRoutingColor(breakdown.routing))}>
            {breakdown.routing === "hazard_confirmed" ? "✓" : 
             breakdown.routing === "peer_corroboration_requested" ? "~" : "?"}
          </Badge>
        )}
      </div>
    );
  }

  // Full breakdown view
  return (
    <div className="space-y-4">
      {/* Header with total score */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold">Report Quality Score</h4>
          <p className="text-sm text-muted-foreground">Skor Kualitas Laporan</p>
        </div>
        <div className="text-right">
          <span className={cn("text-3xl font-bold", getScoreColor(score))}>{score}</span>
          <span className="text-lg text-muted-foreground">/100</span>
          {breakdown?.routing && (
            <Badge className={cn("ml-2", getRoutingColor(breakdown.routing))}>
              {getRoutingLabel(breakdown.routing)}
            </Badge>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-3 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            "h-full transition-all duration-500",
            score >= 55 ? "bg-success" : score >= 25 ? "bg-warning" : "bg-destructive"
          )}
          style={{ width: `${score}%` }}
        />
      </div>

      {/* Detailed breakdown */}
      {breakdown && (
        <div className="space-y-2 pt-2 border-t">
          <h5 className="text-sm font-medium text-muted-foreground">Calculation Breakdown:</h5>
          
          {/* Photo Evidence */}
          {breakdown.photo_present && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-muted-foreground" />
                <span>Photo Evidence</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={breakdown.photo_present.earned ? "text-success" : "text-muted-foreground"}>
                  {breakdown.photo_present.points}/{breakdown.photo_present.max}
                </span>
                {breakdown.photo_present.earned ? 
                  <CheckCircle2 className="h-4 w-4 text-success" /> : 
                  <XCircle className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          )}

          {/* Photo AI Confidence */}
          {breakdown.photo_confidence && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-muted-foreground" />
                <span>AI Confidence</span>
                <span className="text-xs text-muted-foreground">
                  ({(breakdown.photo_confidence.ai_confidence * 100).toFixed(0)}%)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={breakdown.photo_confidence.points > 0 ? "text-success" : "text-muted-foreground"}>
                  {breakdown.photo_confidence.points}/{breakdown.photo_confidence.max}
                </span>
                {breakdown.photo_confidence.points >= 15 ? 
                  <CheckCircle2 className="h-4 w-4 text-success" /> : 
                  breakdown.photo_confidence.points >= 8 ?
                  <AlertTriangle className="h-4 w-4 text-warning" /> :
                  <XCircle className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          )}

          {/* Description Quality */}
          {breakdown.description_quality && (
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>Description Quality</span>
                  <span className="text-xs text-muted-foreground">
                    ({breakdown.description_quality.word_count} words)
                  </span>
                </div>
                <span className={breakdown.description_quality.points > 10 ? "text-success" : "text-muted-foreground"}>
                  {breakdown.description_quality.points}/{breakdown.description_quality.max}
                </span>
              </div>
              <div className="ml-6 flex flex-wrap gap-1">
                {breakdown.description_quality.details.location_specific && (
                  <Badge variant="outline" className="text-xs">Location ✓</Badge>
                )}
                {breakdown.description_quality.details.word_count_ok && (
                  <Badge variant="outline" className="text-xs">Length ✓</Badge>
                )}
                {breakdown.description_quality.details.work_activity && (
                  <Badge variant="outline" className="text-xs">Activity ✓</Badge>
                )}
                {breakdown.description_quality.details.cause_provided && (
                  <Badge variant="outline" className="text-xs">Cause ✓</Badge>
                )}
              </div>
            </div>
          )}

          {/* GPS Location */}
          {breakdown.gps_location && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>GPS Location</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={breakdown.gps_location.earned ? "text-success" : "text-muted-foreground"}>
                  {breakdown.gps_location.points}/{breakdown.gps_location.max}
                </span>
                {breakdown.gps_location.earned ? 
                  <CheckCircle2 className="h-4 w-4 text-success" /> : 
                  <XCircle className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          )}

          {/* Reporter Credibility */}
          {breakdown.reporter_credibility && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span>Reporter Credibility</span>
                <span className="text-xs text-muted-foreground">
                  ({breakdown.reporter_credibility.credibility_score}%)
                </span>
              </div>
              <span className={breakdown.reporter_credibility.points >= 10 ? "text-success" : "text-muted-foreground"}>
                {breakdown.reporter_credibility.points}/{breakdown.reporter_credibility.max}
              </span>
            </div>
          )}

          {/* Cause Analysis Bonus */}
          {breakdown.cause_analysis_bonus && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                <span>Cause Analysis Bonus</span>
                {breakdown.cause_analysis_bonus.cause_category && (
                  <Badge variant="outline" className="text-xs capitalize">
                    {breakdown.cause_analysis_bonus.cause_category.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={breakdown.cause_analysis_bonus.earned ? "text-success" : "text-muted-foreground"}>
                  {breakdown.cause_analysis_bonus.points}/{breakdown.cause_analysis_bonus.max}
                </span>
                {breakdown.cause_analysis_bonus.earned ? 
                  <CheckCircle2 className="h-4 w-4 text-success" /> : 
                  <XCircle className="h-4 w-4 text-muted-foreground" />}
              </div>
            </div>
          )}

          {/* Similar reports boost with expandable details */}
          {breakdown.similar_reports_boost != null && breakdown.similar_reports_boost > 0 && (
            <Collapsible open={showSimilarityDetails} onOpenChange={setShowSimilarityDetails}>
              <div className="pt-2 border-t mt-2">
                <CollapsibleTrigger className="flex items-center justify-between text-sm w-full hover:bg-muted/50 rounded p-1 -m-1">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>Similar Reports Boost</span>
                    <Badge variant="outline" className="text-xs">
                      {breakdown.similar_reports_count || 0} matches
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-success">+{breakdown.similar_reports_boost}</span>
                    {showSimilarityDetails ? 
                      <ChevronUp className="h-4 w-4 text-muted-foreground" /> : 
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-2">
                  <p className="text-xs text-muted-foreground ml-6">
                    Reports matched based on: same hazard type + location/worker/recency (min score 7).
                    Boost: 5 pts per match, max +20.
                  </p>
                  {breakdown.similarity_matches && breakdown.similarity_matches.length > 0 && (
                    <div className="ml-6 space-y-1">
                      {breakdown.similarity_matches.slice(0, 3).map((match, idx) => (
                        <div key={idx} className="flex flex-wrap gap-1 text-xs">
                          <span className="text-muted-foreground">Match {idx + 1}:</span>
                          {match.match_factors.map((factor, fidx) => (
                            <Badge key={fidx} variant="secondary" className="text-xs py-0">
                              {getMatchFactorLabel(factor)}
                            </Badge>
                          ))}
                          <span className="text-muted-foreground">(score: {match.similarity_score})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Peer corroboration boost */}
          {breakdown.peer_corroboration_boost != null && breakdown.peer_corroboration_boost > 0 && (
            <div className="flex items-center justify-between text-sm pt-2 border-t mt-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                <span>Peer Corroboration Boost</span>
              </div>
              <span className="text-success">+{breakdown.peer_corroboration_boost}</span>
            </div>
          )}
        </div>
      )}

      {/* Routing explanation */}
      <div className="text-xs text-muted-foreground pt-2 border-t">
        <p>
          {score >= 55 && "✓ Kualitas tinggi - laporan dikonfirmasi otomatis"}
          {score >= 25 && score < 55 && "~ Kualitas sedang - meminta verifikasi dari rekan kerja"}
          {score < 25 && "✗ Kualitas rendah - menunggu laporan serupa"}
        </p>
      </div>
    </div>
  );
}
